import { PublicClient } from 'viem'

import { WRAPPED_GAS_TOKENS } from '@src/configs/chainInfo'
import { EVM_PROD_WALLET } from '@src/configs/publickeys'
import { getEVMGasFeeMetrics } from '@src/hooks/evm/useGasFeeMetrics'
import { getViemClient, viemChainConfigs } from '@src/hooks/evm/useViemClient'
import { adjustWrapFee } from '@src/hooks/usePrecomputedGasPrices'
import { ContractWriteParams, GasFeeMetrics, GasFeeSimulationResults } from '@src/types/gasFees'
import { PrecomputeGasResults, TransactionType } from '@src/types/gasPrices'
import { getChecksumAddress } from '@src/utils/address'
import {
    APPROVE_SIGNATURE,
    DEPOSIT_FOR_BURN_SIGNATURE,
    PLACE_ORDER_SIGNATURE_BYTES32,
    WRAP_GAS_SIGNATURE,
} from '@src/utils/functionSignatures'
import { createContextLogger } from '@src/utils/logger'
import { simulateGasFees } from '@src/utils/simulateGasFees'
import { SwapContractType } from '@src/zustand_stores/config.store'

const PRINT_GAS_ERRORS = false
const logger = createContextLogger('usePrecomputedEVMGasPrices')

export const usePrecomputedEVMGasPrices = (swapContracts: Record<string, any> | undefined) => {
    const computeEVMGasPrices = async (): Promise<PrecomputeGasResults> => {
        if (!swapContracts) {
            PRINT_GAS_ERRORS && logger.error(` swapContracts not found`)
            return {} as PrecomputeGasResults
        }

        // Process all chains in parallel
        const chainPromises = Object.keys(viemChainConfigs).map(async chainId => {
            const numericChainId = +chainId
            const result = {
                feeMetrics: {} as GasFeeMetrics,
                results: {
                    [TransactionType.WRAP]: {} as GasFeeSimulationResults,
                    [TransactionType.APPROVE]: {} as GasFeeSimulationResults,
                    [TransactionType.PLACE_ORDER]: {} as GasFeeSimulationResults,
                    [TransactionType.TRANSFER]: {} as GasFeeSimulationResults,
                    [TransactionType.DEPOSIT_FOR_BURN]: {} as GasFeeSimulationResults,
                },
            }

            try {
                const publicClient = getViemClient(numericChainId) as PublicClient

                // Run all simulations in parallel
                const [gasFeeMetrics, wrapFee, approveFee, placeOrderFee, depositForBurnFee] = await Promise.all([
                    getEVMGasFeeMetrics(publicClient),
                    simulateWrapTx(numericChainId, publicClient).catch(e => {
                        PRINT_GAS_ERRORS &&
                            logger.error(`Unable to simulate wrap transaction for chain ${numericChainId}`, e)
                        return null
                    }),
                    simulateApproveTx(numericChainId, publicClient).catch(e => {
                        PRINT_GAS_ERRORS &&
                            logger.error(` chain (${chainId}) unable to simulate approve transaction.`, e)
                        return null
                    }),
                    simulatePlaceOrderTx(numericChainId, publicClient, swapContracts).catch(e => {
                        PRINT_GAS_ERRORS &&
                            console.error(
                                `[usePrecomputedGasPrices] chain (${chainId}) unable to simulate placeOrder transaction.`,
                                e
                            )
                        return null
                    }),
                    simulateDepositForBurnTx(numericChainId, publicClient, swapContracts).catch(e => {
                        PRINT_GAS_ERRORS &&
                            console.warn(
                                `[usePrecomputedGasPrices] chain (${chainId}) unable to simulate depositForBurn transaction.`,
                                e
                            )
                        return simulateBackupDepositForBurnTx(numericChainId, publicClient)
                    }),
                ])

                result.feeMetrics = gasFeeMetrics
                if (wrapFee) result.results[TransactionType.WRAP] = wrapFee
                if (approveFee) result.results[TransactionType.APPROVE] = approveFee
                if (placeOrderFee) result.results[TransactionType.PLACE_ORDER] = placeOrderFee
                if (depositForBurnFee) result.results[TransactionType.DEPOSIT_FOR_BURN] = depositForBurnFee
            } catch (err) {
                PRINT_GAS_ERRORS && logger.error(` chain (${chainId}) ERROR:`, err)
            }

            return { [numericChainId]: result }
        })

        // Combine all chain results
        const chainResults = await Promise.all(chainPromises)
        return chainResults.reduce((acc, curr) => ({ ...acc, ...curr }), {})
    }

    return { computeEVMGasPrices }
}

// Helper functions (add these after the main function)
async function simulateWrapTx(chainId: number, publicClient: PublicClient) {
    const wrap_tx: ContractWriteParams = {
        chainId,
        contractAddress: WRAPPED_GAS_TOKENS[chainId].address,
        functionSignature: WRAP_GAS_SIGNATURE,
        functionParams: [],
        value: BigInt(1000000000),
        caller: EVM_PROD_WALLET,
    }
    const gasFeeMetrics = await getEVMGasFeeMetrics(publicClient)
    const wrap_fee = await simulateGasFees([wrap_tx], gasFeeMetrics, publicClient)
    return {
        ...wrap_fee,
        totalGasUnits: adjustWrapFee(wrap_fee.totalGasUnits, chainId),
        indivdualGasUnits: [adjustWrapFee(wrap_fee.indivdualGasUnits[0], chainId)],
    }
}

async function simulateApproveTx(chainId: number, publicClient: PublicClient) {
    const dummy_guy = getChecksumAddress('0xDEAD'.padEnd(42, 'f'))
    const approve_tx: ContractWriteParams = {
        chainId,
        contractAddress: WRAPPED_GAS_TOKENS[chainId].address,
        functionSignature: APPROVE_SIGNATURE,
        functionParams: [
            { type: 'address', value: dummy_guy },
            { type: 'uint256', value: '0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff' },
        ],
        value: BigInt(0),
        caller: EVM_PROD_WALLET,
    }
    const approve_fee = await simulateGasFees([approve_tx], await getEVMGasFeeMetrics(publicClient), publicClient)
    return approve_fee
}

async function simulatePlaceOrderTx(chainId: number, publicClient: PublicClient, swapContracts: Record<string, any>) {
    const contractAddress = swapContracts[SwapContractType.ORDER_BOOK_V2][chainId]
    if (!contractAddress) {
        throw new Error(`swap contract not found for type ${SwapContractType.ORDER_BOOK_V2} and chainId ${chainId}`)
    }

    // placeOrder((address,address,uint32),(uint96,uint96,uint16,address,uint96),(uint32,uint16,uint16),address,address)
    const a0 = getChecksumAddress('0xa0'.padEnd(42, 'f'))
    const a1 = '0xf'.padEnd(66, 'f')
    const a2 = getChecksumAddress('0xa2'.padEnd(42, 'f'))
    const a3 = '0xf'.padEnd(66, 'f')
    const a4 = getChecksumAddress('0xa4'.padEnd(42, 'f'))

    const uint32_max = '0xffffffff'
    const uint96_max = '0xffffffffffffffffffffffff'

    const transactionFunctionArgs = [
        { type: 'tuple', value: [a0, a1, uint32_max] },
        { type: 'tuple', value: [uint96_max, uint96_max, '0', a2, uint96_max] },
        { type: 'tuple', value: ['1735217068', '30', '50'] },
        { type: 'address', value: a3 },
        { type: 'address', value: a4 },
    ]

    const placeOrder_tx: ContractWriteParams = {
        chainId,
        contractAddress: contractAddress,
        functionSignature: PLACE_ORDER_SIGNATURE_BYTES32,
        functionParams: transactionFunctionArgs,
        value: BigInt(0),
        caller: EVM_PROD_WALLET,
    }

    const placeOrder_fee = await simulateGasFees([placeOrder_tx], await getEVMGasFeeMetrics(publicClient), publicClient)
    return placeOrder_fee
}

async function simulateDepositForBurnTx(
    chainId: number,
    publicClient: PublicClient,
    swapContracts: Record<string, any>
) {
    const dummy_guy = getChecksumAddress('0xDEAD'.padEnd(42, 'f'))
    const depositForBurn_tx: ContractWriteParams = {
        chainId,
        contractAddress: swapContracts[SwapContractType.CCTP_TOKEN_MESSENGER][chainId],
        functionSignature: DEPOSIT_FOR_BURN_SIGNATURE,
        functionParams: [
            { type: 'uint256', value: '0x1' }, // Can't simulate with 0 amount because it reverts with "Amount must be nonzero"
            { type: 'uint32', value: '0x5' }, // Must use a valid domain
            { type: 'bytes32', value: '0x'.padEnd(66, 'f') },
            { type: 'address', value: dummy_guy },
        ],
        value: BigInt(0),
        caller: EVM_PROD_WALLET,
    }
    const depositForBurn_fee = await simulateGasFees(
        [depositForBurn_tx],
        await getEVMGasFeeMetrics(publicClient),
        publicClient
    )
    return depositForBurn_fee
}

async function simulateBackupDepositForBurnTx(chainId: number, publicClient: PublicClient) {
    // TODO: If simulation of depositForBurn_tx reverts due to low allowance,
    // Approximate its gas costs by the costs of an approve + 30% buffer.
    const dummy_guy = getChecksumAddress('0xDEAD'.padEnd(42, 'f'))
    const approve_tx: ContractWriteParams = {
        chainId,
        contractAddress: WRAPPED_GAS_TOKENS[chainId].address,
        functionSignature: APPROVE_SIGNATURE,
        functionParams: [
            { type: 'address', value: dummy_guy },
            { type: 'uint256', value: '0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff' },
        ],
        value: BigInt(0),
        caller: EVM_PROD_WALLET,
    }
    const approve_fee = await simulateGasFees([approve_tx], await getEVMGasFeeMetrics(publicClient), publicClient)

    const approve_fee_with_buffer = {
        totalGasUnits: (approve_fee.totalGasUnits * BigInt(13000)) / BigInt(10000),
        indivdualGasUnits: [(approve_fee.totalGasUnits * BigInt(13000)) / BigInt(10000)],
        totalFeePerGasUnit: approve_fee.totalFeePerGasUnit,
        feeMetrics: approve_fee.feeMetrics,
        tronResults: approve_fee.tronResults,
    }

    return approve_fee_with_buffer
}
